import axios, { type AxiosRequestConfig, type CancelTokenSource } from 'axios'

// axios 기본 설정
axios.defaults.withCredentials = true
axios.defaults.headers.common['Content-Type'] = 'application/json'

// 요청 취소를 위한 맵
const pendingRequests = new Map<string, CancelTokenSource>()

// 요청 키 생성 함수
const generateRequestKey = (config: AxiosRequestConfig): string => {
  const { method, url, params, data } = config
  return [method, url, JSON.stringify(params), JSON.stringify(data)].join('&')
}

// 이전 요청 취소 함수
export const cancelPendingRequest = (config: AxiosRequestConfig) => {
  const requestKey = generateRequestKey(config)
  
  if (pendingRequests.has(requestKey)) {
    const cancelToken = pendingRequests.get(requestKey)
    cancelToken?.cancel('Request cancelled due to duplicate request')
    pendingRequests.delete(requestKey)
  }
}

// 요청 추가 함수
const addPendingRequest = (config: AxiosRequestConfig) => {
  const requestKey = generateRequestKey(config)
  
  // 동일한 요청이 진행 중이면 취소
  cancelPendingRequest(config)
  
  // 새 취소 토큰 생성
  const source = axios.CancelToken.source()
  config.cancelToken = source.token
  pendingRequests.set(requestKey, source)
}

// 요청 제거 함수
const removePendingRequest = (config: AxiosRequestConfig) => {
  const requestKey = generateRequestKey(config)
  pendingRequests.delete(requestKey)
}

// 요청 인터셉터
axios.interceptors.request.use(
  (config) => {
    // 토큰이 있으면 헤더에 추가 (auth_token, token, access_token 순으로 확인)
    let token = localStorage.getItem('auth_token') || localStorage.getItem('token') || localStorage.getItem('access_token')

    // localStorage에 없으면 쿠키에서 읽기
    if (!token) {
      const getCookieToken = () => {
        const cookies = document.cookie.split(';')
        for (const cookie of cookies) {
          const [name, value] = cookie.trim().split('=')
          if (name === 'jwt_token') {
            return value
          }
        }
        return null
      }
      token = getCookieToken()
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // FormData인 경우 Content-Type 헤더 삭제 (브라우저가 자동으로 multipart/form-data 설정)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }

    // AbortController 지원 확인 (signal이 있으면 CancelToken 대신 사용)
    if (!config.signal) {
      // 중복 요청 취소 (GET 요청에만 적용)
      if (config.method?.toLowerCase() === 'get') {
        addPendingRequest(config)
      }
    }
    
    return config
  },
  (error) => Promise.reject(error)
)

// 토큰 갱신 관련 변수
let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (error: unknown) => void
}> = []

// 대기 중인 요청들 처리
const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token!)
    }
  })
  failedQueue = []
}

// 로그아웃 처리
const handleLogout = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('auth_token')
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
  localStorage.removeItem('user')

  // 로그인 페이지가 아닌 경우에만 리다이렉트
  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login'
  }
}

// 응답 인터셉터
axios.interceptors.response.use(
  (response) => {
    // 완료된 요청 제거
    removePendingRequest(response.config)
    return response
  },
  async (error) => {
    // 취소된 요청 처리
    if (axios.isCancel(error)) {
      return Promise.reject(error)
    }

    // 에러 발생 시에도 요청 제거
    if (error.config) {
      removePendingRequest(error.config)
    }


    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }

    // 401 에러이고 재시도하지 않은 경우 토큰 갱신 시도
    if (error.response?.status === 401 && !originalRequest._retry) {
      // 토큰 갱신 API 자체가 401인 경우는 로그아웃
      if (originalRequest.url?.includes('/api/auth/refresh')) {
        handleLogout()
        return Promise.reject(error)
      }

      // 이미 토큰 갱신 중이면 대기
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers = originalRequest.headers || {}
            originalRequest.headers.Authorization = `Bearer ${token}`
            return axios(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // Refresh 토큰으로 새 Access 토큰 발급
        const refreshToken = localStorage.getItem('refresh_token')
        if (!refreshToken) {
          throw new Error('No refresh token')
        }

        const response = await axios.post('/api/auth/refresh/', {
          refresh: refreshToken,
        })

        const newAccessToken = response.data.access
        const newRefreshToken = response.data.refresh // 회전된 새 refresh 토큰

        // 새 토큰 저장
        localStorage.setItem('auth_token', newAccessToken)
        localStorage.setItem('access_token', newAccessToken)
        localStorage.setItem('token', newAccessToken)  // 2026-11-03: 옛 'token' 키도 동기화(엑셀 다운로드 401 방지)
        if (newRefreshToken) {
          localStorage.setItem('refresh_token', newRefreshToken)
        }

        // 대기 중인 요청들 처리
        processQueue(null, newAccessToken)

        // 원래 요청 재시도
        originalRequest.headers = originalRequest.headers || {}
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        return axios(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        handleLogout()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export default axios